import * as Sentry from "@sentry/gatsby"
import { datadogRum } from "@datadog/browser-rum"
import { getCachedUser } from "./cached-user"
import HeapsError from "../util/heaps-error"
import { errorCodes } from "./error-codes"
import { toCustomer } from "./datadog-rum"
import type { NextActionType } from "../models/payment-methods"

/**
 * Failed calls to our API or third-party services, categorized by the API
 * domain the call belongs to. "unknown" is the fallback for endpoints the
 * mapping in api-error-classification.ts does not recognize yet — treat
 * occurrences as a prompt to extend the mapping.
 */
export type NetworkErrorCategory =
  | "payment"
  | "order"
  | "venues"
  | "user"
  | "auth"
  | "products"
  | "product-labels"
  | "unknown"

/**
 * Rendering and component failures:
 * - "render": a component crashed while rendering (error boundaries)
 * - "invisible-error": something went wrong without any feedback shown to
 *   the user
 * - "data-mismatch": the UI received data it cannot present correctly
 */
export type UiErrorCategory = "render" | "invisible-error" | "data-mismatch"

/**
 * Broken invariants and unexpected states:
 * - "missing-validation": input got past a validation that should have
 *   caught it
 * - "unauthorized-access-attempted": an action was attempted without the
 *   required access
 * - "invalid-state": the app reached a state that should be impossible
 * - "misconfiguration": build/environment/admin configuration is wrong or
 *   missing
 * - "unsupported-operation": a flow hit a case we do not support (e.g. an
 *   unknown payment provider)
 */
export type BusinessLogicErrorCategory =
  | "missing-validation"
  | "unauthorized-access-attempted"
  | "invalid-state"
  | "misconfiguration"
  | "unsupported-operation"

/** Where in the payment flow the failure happened. */
export type PaymentStep =
  | "initiate-payment"
  | "create-transaction"
  | "provider-action"
  // The 3DS/SCA consumer challenge (Transpayrent calls this "authenticate")
  | "sca-challenge"
  | "authorize"
  | "register-card"
  | "redirect-return"
  | "status-polling"
  | "cancel-payment"
  | "add-card"

export type PaymentProvider = NextActionType["providerName"]

/**
 * Which system we were talking to when the call failed. Lets you separate
 * "our backend is broken" from "a third party is broken", which are different
 * problems with different owners.
 */
export type NetworkTarget =
  | "internal-api"
  | "external-api"
  // Device connectivity: no specific system was reached at all
  | "unknown"

/**
 * The valid categories depend on the type, enforced by the compiler:
 * e.g. { type: "ui", category: "payment" } does not compile. Payment errors
 * must additionally say where in the flow they happened (`step`) and may
 * name the payment provider involved. Sent as the `errorType`,
 * `errorCategory`, `errorStep` and `paymentProvider` tags in Sentry and as
 * context attributes in Datadog, so both interfaces can group, filter and
 * alert on them.
 */
export type ErrorClassification =
  | {
      type: "network"
      target: NetworkTarget
      category: "payment"
      step: PaymentStep
      /** Omit only when no provider is involved yet or it is unknown. */
      provider?: PaymentProvider
    }
  | {
      type: "network"
      target: NetworkTarget
      category: Exclude<NetworkErrorCategory, "payment">
    }
  | { type: "ui"; category: UiErrorCategory }
  | { type: "business_logic"; category: BusinessLogicErrorCategory }

export type CaptureOptions = ErrorClassification & {
  /**
   * Anything that helps the investigation. Primitive values become Sentry
   * tags (filterable); objects (e.g. response bodies) go to Sentry "extra".
   * Datadog receives everything as searchable error context attributes.
   */
  context?: Record<string, unknown>
}

// Not forwarded to Datadog. Sentry keeps receiving these for now to preserve
// the existing support workflow; drop them entirely once that is confirmed
// unnecessary or once Sentry is decommissioned.
const DATADOG_EXCLUDED_KEYS = ["email", "phoneNumber"]

// Each error instance is captured at most once, so the fetch layer, React
// Query's global handler and flow-level catches can all capture defensively
// without producing duplicate events. The first capture (closest to the
// failure, with the richest classification) wins.
const capturedErrors = new WeakSet<object>()

const isPrimitive = (value: unknown): value is string | number | boolean =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"

export const captureException = (
  exception: unknown,
  options: CaptureOptions
) => {
  // A refused session is a state the checkout pages handle (see On401), not
  // a bug - it would otherwise be logged from every catch block it passes
  if (
    exception instanceof HeapsError &&
    (exception.errorCode === errorCodes.SESSION_EXPIRED_USER ||
      exception.errorCode === errorCodes.SESSION_EXPIRED_GUEST)
  ) {
    return
  }

  if (exception !== null && typeof exception === "object") {
    if (capturedErrors.has(exception)) {
      return
    }
    capturedErrors.add(exception)
  }

  const { category, type, context } = options

  if (process.env.GATSBY_NODE_ENV !== "production") {
    console.log(`Logging error! [${type}/${category}]`)
    console.log(exception)
    return
  }

  const cachedUser = getCachedUser()
  const session_id =
    cachedUser && "session_id" in cachedUser ? cachedUser.session_id : ""

  const heapsErrorContext =
    exception instanceof HeapsError
      ? {
          errorCode: exception.errorCode,
          statusCode: exception.statusCode,
          ...(exception.request && {
            method: exception.request.method,
            endpoint: exception.request.endpoint,
            responseBody: exception.request.responseBody,
          }),
        }
      : {}

  const sharedContext: Record<string, unknown> = {
    ...heapsErrorContext,
    ...context,
    errorCategory: category,
    errorType: type,
    ...("target" in options && { errorTarget: options.target }),
    // Marks deliberately generated verification errors so they can be
    // excluded from dashboards and alerts in both tools
    ...(process.env.GATSBY_DATADOG_TEST_SESSION === "true" && {
      isTestSession: true,
    }),
    ...("step" in options && { errorStep: options.step }),
    ...("provider" in options &&
      options.provider && { paymentProvider: options.provider }),
    ...(session_id && { sessionId: session_id }),
  }

  const tags: Record<string, string | number | boolean> = {}
  const extra: Record<string, unknown> = {}
  Object.entries(sharedContext).forEach(([key, value]) => {
    if (value === undefined) {
      return
    }
    if (isPrimitive(value)) {
      tags[key] = value
    } else {
      extra[key] = value
    }
  })

  Sentry.captureException(exception, {
    tags: {
      ...tags,
      version: process.env.GATSBY_APP_VERSION,
      // Datadog gets this from RUM global context; Sentry needs it per event
      ...(typeof window !== "undefined" && {
        customer: toCustomer(window.location.hostname),
      }),
    },
    extra,
  })

  // version/env/service are set once at RUM init, so they are not repeated here
  if (typeof window !== "undefined") {
    const datadogContext = Object.fromEntries(
      Object.entries(sharedContext).filter(
        ([key, value]) =>
          value !== undefined && !DATADOG_EXCLUDED_KEYS.includes(key)
      )
    )
    datadogRum.addError(exception, datadogContext)
  }
}
